/**
 * RTS waterfall bucket definitions.
 *
 * Content is sourced strictly from the SCC RTS Visibility Enhancements product
 * requirements:
 *   - Descriptions: product requirements ("Maps To" column), verbatim
 *   - Source Statuses: the 4 RTS-eligible statuses (DELIVERY_ATTEMPTED, NOT_DELIVERED, PICKED_UP, REJECTED)
 *   - Reason Codes: the 16 RTS-eligible codes, applied only to attempted-volume rows
 *   - Data Sources: upstream event producers (PSS, PTRS, DriftService, IVS, NSS)
 *
 * We do NOT invent packageStatus enum values or event names. If a row lacks a
 * defined source status (e.g., Manifested, Cancelled Pickups), we omit that
 * field — an incomplete tooltip is better than a fabricated one.
 *
 * ACES review status: PENDING — content authored from product requirements,
 * awaiting sign-off from Adam Sherwin, Chris Clark (NA) and Devis Niero,
 * Nathan Milsom (EU).
 */

// RTS-eligible source reason codes (verbatim from product requirements).
export const RTS_ELIGIBLE_REASON_CODES = [
    "ADDRESS_NOT_FOUND", "BAD_WEATHER", "BUSINESS_CLOSED", "CUSTOMER_UNAVAILABLE",
    "DAMAGED", "INACCESSIBLE_DELIVERY_LOCATION", "LATE_DELIVERY", "LOCKER_ISSUE",
    "NO_ITEMS_DELIVERED", "NO_LOCKER_AVAILABLE", "NO_SECURE_LOCATION", "NONE",
    "OBJECT_MISSING", "OTP_NOT_AVAILABLE", "RESCHEDULED_BY_CUSTOMER", "UNSAFE_DUE_TO_DOG"
];

// RTS-eligible source statuses (verbatim from product requirements — the ONLY 4).
export const RTS_ELIGIBLE_SOURCE_STATUSES = [
    "DELIVERY_ATTEMPTED", "NOT_DELIVERED", "PICKED_UP", "REJECTED"
];

// label -> { description, sourceStatuses?, reasonCodes?, dataSource }
export const RTS_BUCKET_DEFINITIONS = {
    // ============================================================
    // Section headers
    // ============================================================
    "Inbound": {
        description: "Volume coming back to the station.",
        dataSource: "PSS"
    },
    "Currently At Station": {
        description: "Packages received at the station being processed.",
        dataSource: "DriftService / IVS / NSS"
    },
    // Custody Transfer — new section representing the SSR / assisted-debrief
    // custody-transfer step. Both sub-rows require upstream indexing pipeline
    // work to populate; the row structure is in place so the UI is
    // forward-compatible with those changes.
    "Custody Transfer": {
        description: "Custody transfer step between driver return and station induct.",
        dataSource: "PTRS / DriftService"
    },

    // ============================================================
    // Custody Transfer sub-rows (placeholders pending indexing pipeline work)
    // ============================================================
    "Self-Serve Return (SSR)": {
        // Descriptor: packages with BTO-SSR
        // status from PTRS. Count updates in near real-time as DPs complete SSR.
        description: "Packages where driver completed Self-Serve Return.",
        dataSource: "PTRS"
    },
    "Associate Debrief": {
        // Descriptor: packages with DriftService Receive confirmation
        // that have NOT yet been inducted.
        description: "Packages received via assisted debrief and awaiting induction.",
        dataSource: "DriftService"
    },
    // Reverse Inducted (Reattemptable + Stowable) — post-induction distinct
    // statuses. Populated once IVS induct events + NOM direction split
    // produces distinct post-induct packageStatus values.
    "Reverse Inducted — Reattemptable": {
        // Descriptor: packages inducted with a forward-stow destination (reattempt).
        description: "Packages inducted for reattempt delivery (forward-stow destination).",
        dataSource: "IVS / NOM"
    },
    "Reverse Inducted — Stowable": {
        // Descriptor: packages inducted with RTFC/Missort/SWA/CRET destination.
        description: "Packages inducted for FC/SC return (RTFC / Missort / SWA / CRET).",
        dataSource: "IVS / NOM"
    },

    // ============================================================
    // Inbound sub-rows — descriptions from product requirements ("Maps To" column)
    // ============================================================
    "Delivery Failed": {
        // At station — specific attempt failure
        description: "Specific attempt failure.",
        sourceStatuses: ["DELIVERY_ATTEMPTED"],
        reasonCodes: RTS_ELIGIBLE_REASON_CODES,
        dataSource: "PSS"
    },
    "Delivery Rejected": {
        // At station — customer refused
        description: "Customer refused the package.",
        sourceStatuses: ["REJECTED"],
        reasonCodes: RTS_ELIGIBLE_REASON_CODES,
        dataSource: "PSS"
    },
    "Manifested": {
        // Inbound — customer return manifested for pickup
        description: "Customer return manifested for pickup.",
        // No sourceStatuses — MANIFESTED is not one of the 4 RTS-eligible statuses.
        dataSource: "PSS"
    },
    "In Transit (Seller -> DS)": {
        // Inbound — SWA/MFN en route
        description: "SWA/MFN en route.",
        sourceStatuses: ["PICKED_UP"],
        dataSource: "PSS"
    },
    "In Transit (XPT -> DS)": {
        // Not explicitly defined for XPT direction; description kept minimal pending ACES review.
        description: "Reverse volume in transit.",
        sourceStatuses: ["PICKED_UP"],
        dataSource: "PSS"
    },
    "In Transit (Customer/Locker -> DS)": {
        // Inbound — P180/Counter return en route
        description: "P180/Counter return en route.",
        sourceStatuses: ["PICKED_UP"],
        dataSource: "PSS"
    },
    "Cancelled Pickups": {
        // Remove — doesn't result in RTS volume
        description: "Doesn't result in RTS volume.",
        dataSource: "PSS"
    },
    "Pickup failed": {
        // Remove — retain as separate card only
        description: "Package doesn't arrive at station for RTS processing.",
        dataSource: "PSS"
    },
    "Delivery Cancelled": {
        // Acceptance criteria: "'Delivery Cancelled' packages move from the
        // 'Currently at Station' section to the 'Inbound' section". Description reflects
        // the product requirement to group these with inbound volume.
        description: "Delivery cancelled — grouped with inbound volume.",
        dataSource: "PSS"
    },

    // ============================================================
    // Currently At Station sub-rows — descriptions from product requirements
    // ============================================================
    "Received (DS -> FC)": {
        // Received for FC return (reverse stowable)
        description: "Received for FC return (reverse stowable).",
        dataSource: "DriftService"
    },
    "RTS Received (DS -> Customer)": {
        // Debriefed reattemptable, pending induct
        description: "Debriefed reattemptable, pending induct.",
        dataSource: "DriftService"
    },
    "Marked for Reprocess (DS -> FC)": {
        // Pending re-induction
        description: "Pending re-induction.",
        dataSource: "IVS"
    },
    "Undeliverable": {
        // At station — composite attempted bucket
        description: "Composite attempted-volume bucket.",
        sourceStatuses: ["DELIVERY_ATTEMPTED", "NOT_DELIVERED", "REJECTED"],
        reasonCodes: RTS_ELIGIBLE_REASON_CODES,
        dataSource: "PSS"
    },
    "Stowed": {
        // Successfully processed
        description: "Successfully processed.",
        dataSource: "NSS"
    },
    "Pending Verification (DS -> FC)": {
        // Not explicitly defined; description kept minimal pending ACES review.
        description: "Received, awaiting verification.",
        dataSource: "DriftService"
    }
};

/**
 * Returns the definition for a raw rtsRowMapping label, or null if none is defined
 * (callers should render no tooltip rather than an empty one).
 */
export function getRtsBucketDefinition(label) {
    if (!label) return null;
    return RTS_BUCKET_DEFINITIONS[label] || null;
}

/**
 * Formats a definition into a human-readable tooltip string per 
 *   "<description> | Statuses: A, B | Reason codes: X, Y | Source: PSS"
 *
 * Only includes fields the product requirements explicitly specify: source
 * statuses (from the 4 RTS-eligible statuses), source reason codes (from the
 * 16 RTS-eligible codes), and data source.
 */
export function formatBucketTooltip(label) {
    const def = getRtsBucketDefinition(label);
    if (!def) return null;
    const parts = [];
    if (def.description) parts.push(def.description);
    if (def.sourceStatuses && def.sourceStatuses.length) {
        parts.push("Statuses: " + def.sourceStatuses.join(", "));
    }
    if (def.reasonCodes && def.reasonCodes.length) {
        parts.push("Reason codes: " + def.reasonCodes.join(", "));
    }
    if (def.dataSource) parts.push("Source: " + def.dataSource);
    return parts.join(" | ");
}



// WEBPACK FOOTER //
// ./src/commons/dataStore/RTSBucketDefinitions.js